export interface AgendaDueRow {
    touchpointId: string;
    contactId: string;
    contactName: string;
    orgName?: string | null;
    contactEmail?: string | null;
    contactPhone?: string | null;
    contactEmailOptedOut: boolean;
    tierListItemId: string;
    tierName?: string | null;
    tierColor?: string | null;
    kinds: string[];
    intervalCount: number;
    intervalUnit: string;
    lastContactedAt?: string | null;
}

export interface AgendaTouchpoint {
    touchpointId: string;
    kinds: string[];
    intervalCount: number;
    intervalUnit: string;
    lastContactedAt: string | null;
}

export interface AgendaContactGroup {
    contactId: string;
    contactName: string;
    orgName: string | null;
    contactEmail: string | null;
    contactPhone: string | null;
    contactEmailOptedOut: boolean;
    tierListItemId: string;
    tierName: string | null;
    tierColor: string | null;
    touchpoints: AgendaTouchpoint[];
}

export const groupDueTouchpoints = (rows: AgendaDueRow[]): AgendaContactGroup[] => {
    const groups: AgendaContactGroup[] = [];
    const byContact = new Map<string, AgendaContactGroup>();
    for (const row of rows) {
        const existing = byContact.get(row.contactId);
        const group = existing ?? createGroup(row);
        if (!existing) {
            byContact.set(row.contactId, group);
            groups.push(group);
        }
        group.touchpoints.push({
            touchpointId: row.touchpointId,
            kinds: row.kinds,
            intervalCount: row.intervalCount,
            intervalUnit: row.intervalUnit,
            lastContactedAt: row.lastContactedAt ?? null,
        });
    }
    return groups;
};

const createGroup = (row: AgendaDueRow): AgendaContactGroup => ({
    contactId: row.contactId,
    contactName: row.contactName,
    orgName: row.orgName ?? null,
    contactEmail: row.contactEmail ?? null,
    contactPhone: row.contactPhone ?? null,
    contactEmailOptedOut: row.contactEmailOptedOut,
    tierListItemId: row.tierListItemId,
    tierName: row.tierName ?? null,
    tierColor: row.tierColor ?? null,
    touchpoints: [],
});

export const filterGroupsByTier = (
    groups: AgendaContactGroup[],
    selectedTierIds: string[],
): AgendaContactGroup[] => {
    if (selectedTierIds.length === 0) {
        return groups;
    }
    const selected = new Set(selectedTierIds);
    return groups.filter(group => selected.has(group.tierListItemId));
};

export interface AgendaSnoozeRef {
    contactId: string;
    touchpointId: string | null;
}

export interface AgendaCompletedTouchpoint {
    touchpointId: string;
    contactId: string;
    contactName: string;
    orgName: string | null;
    tierName: string | null;
    tierColor: string | null;
    kinds: string[];
    scheduledVisit: boolean;
}

export const dueRowKey = (row: { contactId: string; touchpointId: string }): string =>
    `${row.contactId}|${row.touchpointId}`;

export const computeNewlyCompleted = (
    previousRows: AgendaDueRow[],
    currentRows: AgendaDueRow[],
    currentSnoozes: AgendaSnoozeRef[],
    contactsWithUpcomingVisit: Set<string>,
): AgendaCompletedTouchpoint[] => {
    const stillDue = new Set(currentRows.map(dueRowKey));
    const snoozedContacts = new Set(
        currentSnoozes
            .filter(snooze => snooze.touchpointId === null)
            .map(snooze => snooze.contactId),
    );
    const snoozedTouchpoints = new Set(
        currentSnoozes
            .filter(
                (snooze): snooze is { contactId: string; touchpointId: string } =>
                    snooze.touchpointId !== null,
            )
            .map(dueRowKey),
    );

    return previousRows
        .filter(row => !stillDue.has(dueRowKey(row)))
        .filter(row => !snoozedContacts.has(row.contactId))
        .filter(row => !snoozedTouchpoints.has(dueRowKey(row)))
        .map(row => ({
            touchpointId: row.touchpointId,
            contactId: row.contactId,
            contactName: row.contactName,
            orgName: row.orgName ?? null,
            tierName: row.tierName ?? null,
            tierColor: row.tierColor ?? null,
            kinds: row.kinds,
            scheduledVisit:
                row.kinds.includes("visit") &&
                contactsWithUpcomingVisit.has(row.contactId),
        }));
};

export type EmailActionState = "send-email" | "add-email" | "email-opted-out";

export const resolveEmailAction = (input: {
    contactEmail: string | null;
    contactEmailOptedOut: boolean;
}): EmailActionState => {
    if (input.contactEmailOptedOut) {
        return "email-opted-out";
    }
    if (input.contactEmail) {
        return "send-email";
    }
    return "add-email";
};

export interface TouchpointActions {
    email: EmailActionState | null;
    addContactPhone: boolean;
    provisionPhoneLine: boolean;
    call: boolean;
    text: boolean;
    visit: boolean;
}

export const resolveTouchpointActions = (input: {
    kinds: string[];
    contactEmail: string | null;
    contactEmailOptedOut: boolean;
    hasContactPhone: boolean;
    hasPhoneLine: boolean;
    visitsEnabled: boolean;
}): TouchpointActions => {
    const wantsCall = input.kinds.includes("call");
    const wantsText = input.kinds.includes("text");
    const wantsPhone = wantsCall || wantsText;
    const callable = wantsPhone && input.hasContactPhone && input.hasPhoneLine;
    return {
        email: input.kinds.includes("email") ? resolveEmailAction(input) : null,
        addContactPhone: wantsPhone && !input.hasContactPhone,
        provisionPhoneLine: wantsPhone && input.hasContactPhone && !input.hasPhoneLine,
        call: wantsCall && callable,
        text: wantsText && callable,
        visit: input.visitsEnabled && input.kinds.includes("visit"),
    };
};

export const agendaNeedsPhoneProvisioning = (
    groups: AgendaContactGroup[],
    hasPhoneLine: boolean,
): boolean => {
    if (hasPhoneLine) {
        return false;
    }
    return groups.some(group =>
        group.touchpoints.some(
            touchpoint =>
                touchpoint.kinds.includes("call") || touchpoint.kinds.includes("text"),
        ),
    );
};
